// Copyright (c) 2025 Audere, All Rights Reserved

import { Box, Paper } from "@mui/material";
import type {
  ConfusionMatrixProps,
  FilteredConfusionMatrixKey,
  PredictionDataKey,
} from "../types/projects";
import { useTranslation } from "react-i18next";
import { createStyleSheet } from "../utils/styles";
import { useMemo, Suspense, lazy } from "react";

// Lazy load Plotly to reduce initial bundle size
const Plot = lazy(() => import("./PlotlyWrapper"));

const ConfusionMatrix = ({ confusionMatrixData }: ConfusionMatrixProps) => {
  const { t } = useTranslation("ConfusionMatrix");

  const labels = [
    t("labels.positive"),
    t("labels.negative"),
    t("labels.invalid"),
    t("labels.cant_tell"),
  ];

  const z = useMemo(() => {
    const trueKeys: FilteredConfusionMatrixKey[] = [
      "true_positive",
      "true_negative",
      "true_invalid",
      "true_cant_tell",
    ];

    const predKeys: PredictionDataKey[] = [
      "pred_positive",
      "pred_negative",
      "pred_invalid",
      "pred_cant_tell",
    ];

    return trueKeys.map(trueKey => predKeys.map(predKey => confusionMatrixData[trueKey][predKey]));
  }, [confusionMatrixData]);

  return (
    <Paper elevation={3} sx={styles.container}>
      <Box sx={styles.plotlyWrapper}>
        <Suspense fallback={<Box sx={styles.loadingFallback}>Loading chart...</Box>}>
          <Plot
            data={[
              {
                z: z,
                x: labels,
                y: labels,
                type: "heatmap",
                colorscale: "Blues",
                showscale: true,
                colorbar: {
                  title: { text: t("colorbarTitle") },
                },
                texttemplate: "%{z}",
                textfont: {
                  size: 12,
                  color: "white",
                },
                hoverongaps: false,
              },
            ]}
            useResizeHandler={true}
            layout={{
              title: { text: t("title") },
              xaxis: {
                title: { text: t("xAxisTitle") },
                side: "bottom",
              },
              yaxis: {
                title: { text: t("yAxisTitle") },
                autorange: "reversed",
              },
              height: 500,
              autosize: true,
              margin: {
                l: 80,
                r: 80,
                t: 80,
                b: 80,
              },
            }}
          />
        </Suspense>
      </Box>
    </Paper>
  );
};

export default ConfusionMatrix;

const styles = createStyleSheet({
  container: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
  },
  plotlyWrapper: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& .js-plotly-plot": {
      width: "100% !important",
      maxWidth: "100%",
    },
  },
  loadingFallback: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "500px",
    fontSize: "16px",
    color: "text.secondary",
  },
});
